// features/relatorios/RelatoriosPage.tsx -- Modulo de Relatorios (F23-S06/S07/S08/S10).
import type { CommonReportQuery, ReportScope } from '@elemento/shared-schemas';
import * as React from 'react';

import { useCitiesList } from '../../hooks/useCitiesList';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuth } from '../auth/useAuth';
import { ContextualHelp } from '../help/contextual';

import { ComercialSection } from './comercial/components/ComercialSection';
import { AiSection } from './components/AiSection';
import { AttendanceSection } from './components/AttendanceSection';
import { AuditSection } from './components/AuditSection';
import { CollectionSection } from './components/CollectionSection';
import { CreditSection } from './components/CreditSection';
import { ExportButton } from './components/ExportButton';
import { FunnelSection } from './components/FunnelSection';
import { OverviewSection } from './components/OverviewSection';
import { ProductivitySection } from './components/ProductivitySection';
import { ReportFiltersBar } from './components/ReportFiltersBar';
import { useReportFilters } from './hooks/useReportFilters';
import { useReportsPeople } from './hooks/useReportsPeople';
/**
 * Determina os escopos disponíveis para o scope toggle de /relatorios.
 *
 * Usa cityScopeIds (payload de auth) em vez de heurística por permissão:
 *   - dashboard:read + null (global): admin/gestor_geral → ['global', 'city']
 *   - dashboard:read + [] ou [...] (city-scoped): gestor_regional → ['city']
 *   - dashboard:read_by_agent (sem dashboard:read): agente → ['self']
 *
 * Regra: toggle só aparece com >1 escopo (ReportFiltersBar.showScopeToggle).
 */
function inferAvailableScopes(
  hasPermission: (p: string) => boolean,
  cityScopeIds: string[] | null,
): ReportScope[] {
  if (hasPermission('dashboard:read')) {
    // null = acesso global (admin/gestor_geral)
    if (cityScopeIds === null) return ['global', 'city'];
    // array (vazio ou com cidades) = city-scoped (gestor_regional/agente com dashboard:read)
    return ['city'];
  }
  return ['self'];
}

function inferDefaultScope(
  hasPermission: (p: string) => boolean,
  cityScopeIds: string[] | null,
): ReportScope {
  if (hasPermission('dashboard:read')) {
    if (cityScopeIds === null) return 'global';
    return 'city';
  }
  return 'self';
}
export function RelatoriosPage(): React.JSX.Element {
  const { hasPermission, user } = useAuth();
  const { cities } = useCitiesList();
  const cityScopeIds = user?.cityScopeIds ?? null;
  const availableScopes = inferAvailableScopes(hasPermission, cityScopeIds);
  const defaultScope = inferDefaultScope(hasPermission, cityScopeIds);
  const filters = useReportFilters(defaultScope);
  const canSeeAtendimentos =
    hasPermission('dashboard:read') || hasPermission('dashboard:read_by_agent');
  const canSeeCredito = hasPermission('dashboard:read') || hasPermission('billing:read');
  const canSeeProductividade =
    hasPermission('dashboard:read') || hasPermission('dashboard:read_by_agent');
  const canSeeAuditoria = hasPermission('audit:read');
  // Aba Comercial (F35-S12, doc 26 §16): piloto por permissão + flag. O
  // componente já se auto-oculta sem as duas, mas checamos aqui também para
  // não renderizar o <section>/heading vazio.
  const { enabled: comercialFlagEnabled } = useFeatureFlag('sales.reports.enabled');
  const canSeeComercial = hasPermission('sales_reports:read') && comercialFlagEnabled;
  // O filtro por pessoa e ferramenta de gestor: o backend traduz a pessoa no
  // escopo efetivo dela. Agente (so dashboard:read_by_agent) nao lista colegas.
  const showPersonFilter = hasPermission('dashboard:read');
  const { people } = useReportsPeople(showPersonFilter);
  // Período customizado só vai para a rede com as duas pontas preenchidas e na
  // ordem certa — senão cada tecla no date input viraria um 400. Enquanto isso,
  // a tela mostra o padrão de 30 dias e avisa no rodapé da barra de filtros.
  const customReady =
    filters.dateFrom !== '' && filters.dateTo !== '' && filters.dateFrom <= filters.dateTo;
  const effectiveRange = filters.range === 'custom' && !customReady ? 'last30d' : filters.range;

  const query: Partial<CommonReportQuery> = {
    range: effectiveRange,
    ...(effectiveRange === 'custom' ? { dateFrom: filters.dateFrom, dateTo: filters.dateTo } : {}),
    cityIds: filters.cityIds.length > 0 ? filters.cityIds : undefined,
    personId: filters.personId ?? undefined,
    compareWithPrevious: filters.compareWithPrevious,
  };
  return (
    <div
      className="flex flex-col gap-6"
      style={{
        animation:
          'fade-up var(--dur-slow, 400ms) var(--ease-out, cubic-bezier(0.16,1,0.3,1)) both',
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1
            className="font-display font-bold text-ink"
            style={{
              fontSize: 'var(--text-3xl)',
              letterSpacing: '-0.04em',
              fontVariationSettings: "'opsz' 48",
            }}
          >
            Relatórios
          </h1>
          <p className="font-sans text-sm text-ink-3 mt-1">
            Indicadores operacionais e de desempenho
          </p>
        </div>
        <ContextualHelp featureKey="relatorios.overview" />
        <ExportButton currentSection="overview" filters={filters} />
      </div>
      <ReportFiltersBar
        filters={filters}
        availableScopes={availableScopes}
        availableCities={cities}
        showPersonFilter={showPersonFilter}
        availablePeople={people}
      />
      {filters.range === 'custom' && !customReady && (
        <p className="font-sans text-xs text-ink-3 -mt-3">
          Escolha as duas datas para aplicar o período personalizado. Até lá, os números mostram os
          últimos 30 dias.
        </p>
      )}
      <section>
        <h2
          className="font-display font-bold text-ink mb-4"
          style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
        >
          Visão Geral
        </h2>
        <OverviewSection query={query} />
      </section>
      {canSeeAtendimentos && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Atendimentos & Conversas
          </h2>
          <AttendanceSection query={query} />
        </section>
      )}
      {canSeeAtendimentos && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            IA / Pre-atendimento
          </h2>
          <AiSection query={query} />
        </section>
      )}
      {canSeeAtendimentos && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Funil & CRM
          </h2>
          <FunnelSection query={query} />
        </section>
      )}
      {canSeeCredito && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Crédito
          </h2>
          <CreditSection query={query} />
        </section>
      )}
      {canSeeCredito && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Cobrança & Carteira
          </h2>
          <CollectionSection query={query} />
        </section>
      )}
      {canSeeProductividade && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Produtividade
          </h2>
          <ProductivitySection query={query} />
        </section>
      )}
      {canSeeComercial && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Comercial
          </h2>
          <ComercialSection query={query} />
        </section>
      )}
      {canSeeAuditoria && (
        <section>
          <h2
            className="font-display font-bold text-ink mb-4"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.02em' }}
          >
            Auditoria & Operação
          </h2>
          <AuditSection query={query} />
        </section>
      )}
    </div>
  );
}
